<template>
  <div
    v-if="$store.getters['auth/hasLoanAccess']"
    class="loanPayments"
  >
    <sikanii-list-page
      title="Loan Payments"
      :columns="columnsLoanPayment"
      :rows="loanPayments"
      :table-loading="tableLoading"
      :total-rows="total"
      remote
      show-search
      :show-profile-photo="true"
      :current-page="currentPage"
      :per-page="perPage"
      :show-refresh="false"
      search-placeholder="Type search term + Enter"
      @onSearch="handleSearch"
      @onPageChange="handlePageChange"
      @onPerPageChange="handlePerPageChange"
      @onSortChange="handleSortChange"
    >
      <template slot="table-header">
        <div
          v-responsive="$store.getters['app/smallScreen']"
          class="col-12 mb-1"
        >
          <b-collapse
            id="payment"
          >
            <div class="row">
              <div class="col-6">
                <b-form-checkbox
                  v-if="$ability.can('close-transactions', 'general')"
                  v-model="opened"
                  v-b-tooltip.hover="closed?'Open Transactions, this allows agents to record transactions':'Close Transactions, this prevents agents from recording transactions'"
                  class="custom-control-primary mr-2"
                  switch
                  inline
                  style="margin-top: 8px"
                  @change="closeTransactions"
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="CheckCircleIcon" />
                  </span>
                  <strong>{{ closed?'Closed':'Opened' }}</strong>
                </b-form-checkbox>
              </div>
              <div class="col-6">
                <sikanii-button
                  name="filter_button"
                  icon="FilterIcon"
                  icon-only
                  :variant="filterApplied?'primary':'outline-primary'"
                  tool-tip-text="Filter Transactions"
                  class="mr-1"
                  @click="showTransactionFilterModal()"
                />
              </div>
              <div class="col-md-6 mt-1">
                <sikanii-button
                  name="manage_loan_payment"
                  icon="SettingsIcon"
                  label="Record"
                  tool-tip-text="Record Loan Payments"
                  @click="showLoanPaymentModal()"
                />
              </div>
            </div>
          </b-collapse>
        </div>
        <div
          v-if="filterApplied"
          class="col-12 mb-50"
        >
          <div class="d-flex align-items-center">
            <div
              class="text-primary"
            >
              Showing Loan Payments {{ (filter.start&&filter.end)?'between':'on ' }} <b>{{ filter.start?formatDate(filter.start):'' }}</b> {{ (filter.start&&filter.end)?'and':'' }} <b>{{ filter.end?formatDate(filter.end):'' }}</b>.
            </div>
            <sikanii-button
              label="Clear Filter"
              icon="XIcon"
              variant="flat-danger"
              @click="resetTransactionFilterForm()"
            />
          </div>
        </div>
        <agent-filter @filter="(id)=>getLoanPayments(true,id)" />
      </template>
      <template slot="right-extra-header-actions">
        <div
          v-responsive="$store.getters['app/largeScreen']"
          class="d-flex align-self-center align-items-start"
        >
          <b-form-checkbox
            v-if="$ability.can('close-transactions', 'general')"
            v-model="opened"
            v-b-tooltip.hover="closed?'Open Transactions, this allows agents to record transactions':'Close Transactions, this prevents agents from recording transactions'"
            class="custom-control-primary mr-2"
            switch
            inline
            style="margin-top: 8px"
            @change="closeTransactions"
          >
            <span class="switch-icon-left">
              <feather-icon icon="CheckCircleIcon" />
            </span>
            <strong>{{ closed?'Closed':'Opened' }}</strong>
          </b-form-checkbox>
          <sikanii-button
            name="filter_button"
            icon="FilterIcon"
            icon-only
            :variant="filterApplied?'primary':'outline-primary'"
            tool-tip-text="Filter Transactions"
            class="mr-1"
            @click="showTransactionFilterModal()"
          />
          <sikanii-button
            name="manage_loan_payment"
            icon="SettingsIcon"
            label="Record"
            tool-tip-text="Record Loan Payments"
            @click="showLoanPaymentModal()"
          />
        </div>
        <div
          v-responsive="$store.getters['app/smallScreen']"
          class="d-flex align-self-center align-items-center"
        >
          <sikanii-button
            v-b-toggle.payment
            icon-only
            icon="MoreVerticalIcon"
            variant="flat-primary"
          />
        </div>
      </template>
      <template #first_name="{row}">
        {{ row.item.first_name }} {{ row.item.last_name }}
      </template>
      <template #reference="{row}">
        <span class="mr-50">{{ row.item.reference }}</span>
        <b-badge
          v-if="row.item.is_direct_transfer"
          variant="light-primary"
        >
          <FeatherIcon icon="RepeatIcon" /> transfer
        </b-badge>
      </template>
      <template #amount="{row}">
        <b-badge
          v-if="row.item.reversal"
          variant="light-danger"
        >
          -{{ $store.getters['auth/currency'] }}{{ row.item.amount | currency }}
        </b-badge>
        <b-badge
          v-else
          variant="primary"
          :style="row.item.reversed?'text-decoration:line-through;':''"
        >
          +{{ $store.getters['auth/currency'] }}{{ row.item.amount | currency }}
        </b-badge>
      </template>
      <template #reversal="{row}">
        <b-badge :variant="`${row.item.reversal?'light-info':'light-primary'}`">
          {{
            row.item.reversal?'Reversal':'Normal'
          }}
        </b-badge>
      </template>
      <template #balance="{row}">
        <b-badge variant="light-danger">
          {{ $store.getters['auth/currency'] }}{{ row.item.balance | currency }}
        </b-badge>
      </template>
      <template #action="{row}">
        <sikanii-button
          v-if="!row.item.is_direct_transfer && !row.item.reversed && !row.item.reversal && $ability.can('reverse-loan-payments', 'savings_and_loans')"
          icon="RefreshCwIcon"
          :icon-only="true"
          variant="flat-primary"
          tool-tip-text="Reverse Loan Repayment"
          @click="showReverseConfirmation(row.item.id,row.item.reference)"
        />
        <sikanii-button
          icon="EyeIcon"
          :icon-only="true"
          variant="flat-primary"
          tool-tip-text="View more details"
          @click="showTransactionDetails(row.item)"
        />
      </template>
    </sikanii-list-page>
    <loan-payment-setup
      :modal-opened="loanPaymentModalOpened"
      :edit-id="editLoanPaymentId"
      @modalClosed="closeModal('loanPayment')"
    />
    <sikanii-modal
      title="Confirm Reversal"
      ok-text="Yes, Proceed"
      :show="reverseConfirmationOpened"
      @onClose="()=>{reverseConfirmationOpened=false}"
      @onOk="reverseTransaction()"
    >
      <p>You are about to reverse this transaction(<b>{{ transactionRef }}</b>), please confirm to proceed.</p>
    </sikanii-modal>
    <sikanii-modal
      hide-footer
      :title="`Transaction Details - ${transactionRef}`"
      ok-text="Yes, Proceed"
      :show="transactionDetailsOpened"
      @onClose="()=>{transactionDetailsOpened=false}"
    >
      <div class="row">
        <div class="col-12">
          <b>SOURCE</b>
        </div>
        <div class="col-12">
          <p>{{ paymentSource }}</p>
        </div>
        <div class="col-12">
          <b>NARRATION</b>
        </div>
        <div class="col-12">
          <p>{{ transactionNarration || 'N/A' }}</p>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12">
          <b>DATE AND TIME</b>
        </div>
        <div class="col-12">
          <p>{{ transactionFullDate }}</p>
        </div>
        <div class="col-12 mt-1">
          <b-badge variant="primary">
            <FeatherIcon icon="CheckIcon" />
            Completed Successfully
          </b-badge>
        </div>
      </div>
    </sikanii-modal>
    <sikanii-modal
      title="Filter Transactions"
      ok-text="Apply"
      size="md"
      :ok-disabled="!validFilter || showErrorMessage"
      :show="transactionFilterOpened"
      @onOk="applyFilter()"
      @onClose="()=>{transactionFilterOpened=false}"
    >
      <div class="row">
        <div class="col-12">
          <sikanii-button
            class="float-right"
            name="transaction_action"
            variant="flat-danger"
            icon="XIcon"
            label="Clear Form"
            tool-tip-text="Clear Form"
            @click="resetTransactionFilterForm()"
          />
        </div>
      </div>
      <div
        class="row mb-1"
      >
        <div
          v-if="showErrorMessage"
          class="col-12"
        >
          <small class="text-danger">Please make sure start date and end date are selected (Do not select same date).</small>
        </div>
      </div>
      <b-form-checkbox
        v-model="filterForSpecificDay"
        class="mb-1"
      >
        Filter For Specific Day
      </b-form-checkbox>
      <div class="row align-items-center">
        <div class="col-md-10">
          <sikanii-date-picker
            v-model="transactionDateRangeStart"
            :label="filterForSpecificDay?'Transaction Date':'Transaction Start Date'"
          />
        </div>
        <div class="col-md-2 mt-1">
          <sikanii-button
            class="float-right"
            name="transaction_action"
            variant="flat-danger"
            icon="XIcon"
            icon-only
            @click="clearSingleDate('start')"
          />
        </div>
      </div>
      <div
        v-if="!filterForSpecificDay"
        class="row align-items-center"
      >
        <div class="col-md-10">
          <sikanii-date-picker
            v-model="transactionDateRangeEnd"
            label="Transaction End Date"
          />
        </div>
        <div class="col-md-2 mt-1">
          <sikanii-button
            class="float-right"
            name="transaction_action"
            variant="flat-danger"
            icon="XIcon"
            icon-only
            @click="clearSingleDate('end')"
          />
        </div>
      </div>
    </sikanii-modal>
  </div>
  <div v-else>
    <b-card>
      <div class="text-center">
        <h3 class="text-danger">
          You Do Not Have Access
        </h3>
        <p class="text-primary text-bold">
          Your current subscription does not allow you to access this loans feature.
        </p>
        <p>Consider <a
          href="/account/subscription"
          class="text-primary"
        ><b>Upgrading</b></a></p>
      </div>
    </b-card>
  </div>
</template>

<script>
import SikaniiButton from '@/layouts/components/SikaniiButton.vue'
import showToast from '@/libs/mixins/showToast'
import SikaniiListPage from '@/layouts/components/SikaniiListPage.vue'
import LoanPaymentSetup from '@/views/susu-savings/loan-payments/LoanPaymentSetup.vue'
import confirm from '@/libs/mixins/confirm'
import SikaniiDropdown from '@/layouts/components/SikaniiDropdown.vue'
import SikaniiDropdownItem from '@/layouts/components/SikaniiDropdownItem.vue'
import {
  BBadge, VBTooltip, BFormCheckbox, BCard, VBToggle, BCollapse,
} from 'bootstrap-vue'
import SikaniiModal from '@/layouts/components/SikaniiModal.vue'
import SikaniiDatePicker from '@/layouts/components/SikaniiDatePicker.vue'
import moment from 'moment'
import closeTransactions from '@/libs/mixins/closeTransactions'
import AgentFilter from '@/layouts/components/AgentFilter'

export default {
  name: 'StudentGrading',
  components: {
    AgentFilter,
    SikaniiDatePicker,
    SikaniiModal,
    LoanPaymentSetup,
    SikaniiListPage,
    SikaniiButton,
    BBadge,
    BFormCheckbox,
    BCard,
    BCollapse,
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-toggle': VBToggle,
  },
  mixins: [showToast, confirm, closeTransactions],
  data() {
    return {
      editLoanPaymentId: null,
      loanPaymentModalOpened: false,
      tableLoading: false,
      reverseConfirmationOpened: false,
      transactionRef: null,
      transactionDetailsOpened: false,
      transactionNarration: null,
      transactionFullDate: null,
      // filter
      transactionFilterOpened: false,
      transactionDateRangeStart: null,
      transactionDateRangeEnd: null,
      filterForSpecificDay: false,
      paymentSource: null,
      currentPage: 1,
      perPage: 10,
      searchTerm: '',
      initializing: false,
      sort: {
        column: 'created_at',
        order: 'desc',
      },
    }
  },
  computed: {
    filter() {
      return this.$store.getters['susuSavings/currentPaymentsFilter']
    },
    filterApplied() {
      return !!this.filter.start || !!this.filter.end
    },
    validFilter() {
      return (this.filterForSpecificDay ? this.transactionDateRangeStart : this.validDateRange)
    },
    showErrorMessage() {
      if (this.filterForSpecificDay || (!this.transactionDateRangeStart && !this.transactionDateRangeEnd)) {
        return false
      }
      return !this.validDateRange
    },
    validDateRange() {
      return Date.parse(this.transactionDateRangeStart) < Date.parse(this.transactionDateRangeEnd)
    },
    transactionsOpenedStatus() {
      return this.$store.getters['susuSavings/transactionsOpenedStatus']
    },
    isAdmin() {
      return this.$store.getters['auth/role'] === 'Administrator'
    },
    loanPayments() {
      return this.$store.getters['loans/loanPayments']
    },
    total() {
      return this.$store.getters['loans/totalPaymentsData']
    },
    columnsLoanPayment() {
      const show = this.$store.getters['app/isLargeScreen']
      return [
        {
          key: 'index',
          label: '#',
        },
        {
          key: 'reference',
          label: 'REFERENCE',
        },
        {
          key: 'photo',
          label: 'Photo',
        },
        {
          key: 'customer',
          label: 'Customer',
        },
        {
          key: 'agent',
          label: 'Agent',
        },
        {
          key: 'loan',
          label: 'Loan',
        },
        {
          key: 'amount',
          label: 'Amount Paid',
          sortable: show,
        },
        {
          key: 'balance',
          label: 'Balance',
          sortable: show,
        },
        {
          key: 'reversal',
          label: 'Flow',
        },
        {
          key: 'date',
          label: 'Date',
          sortable: show,
        },
        {
          key: 'action',
          label: 'Action',
        },
      ]
    },
  },
  watch: {
    filterForSpecificDay(val) {
      if (val) {
        this.transactionDateRangeEnd = null
      }
    },
  },
  mounted() {
    this.initializing = true
    this.getLoanPayments()
  },
  methods: {
    clearSingleDate(type) {
      if (type === 'start') {
        this.transactionDateRangeStart = null
      } else {
        this.transactionDateRangeEnd = null
      }
    },
    formatDate(date) {
      return moment(date).format('ll')
    },
    applyFilter() {
      this.$store.commit('susuSavings/setPaymentsFilter', {
        start: this.transactionDateRangeStart,
        end: this.transactionDateRangeEnd,
        specificDay: this.filterForSpecificDay,
      })
      this.transactionFilterOpened = false
      this.getLoanPayments()
    },
    resetTransactionFilterForm() {
      this.transactionDateRangeStart = null
      this.transactionDateRangeEnd = null
      this.$store.commit('susuSavings/setPaymentsFilter', {
        start: null,
        end: null,
      })
      this.getLoanPayments()
    },
    showTransactionDetails(payment) {
      this.transactionNarration = payment.narration
      this.transactionRef = payment.reference
      this.transactionFullDate = payment.full_date
      this.paymentSource = payment.is_direct_transfer ? 'Funds Transfer' : 'Normal Transaction'
      this.transactionDetailsOpened = true
    },
    showReverseConfirmation(id, reference) {
      this.transactionId = id
      this.transactionRef = reference
      this.reverseConfirmationOpened = true
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.is_direct_transfer) return 'table-primary'
    },
    editLoanPayment(id) {
      this.editLoanPaymentId = id
      this.showLoanPaymentModal()
    },
    closeModal(type) {
      // eslint-disable-next-line default-case
      switch (type) {
        case 'loanPayment':
          this.loanPaymentModalOpened = false
          this.editLoanPaymentId = null
          break
      }
    },
    showTransactionFilterModal() {
      this.transactionDateRangeStart = this.filter.start
      this.transactionDateRangeEnd = this.filter.end
      this.filterForSpecificDay = this.filter.specificDay
      this.transactionFilterOpened = true
    },
    showLoanPaymentModal() {
      this.loanPaymentModalOpened = true
    },
    reverseTransaction() {
      this.tableLoading = true
      this.$http.post(`susu-savings/loan-payments/${this.transactionId}/reverse`)
        .then(res => {
          this.$store.commit('loans/addLoanPayment', res.data)
          this.$store.commit('loans/reverseLoanPayment', this.transactionId)
        })
        .catch(err => {
          if (err.response.status === 403) {
            this.showInfo('You are not authorized to reverse this transaction')
          } else if (err.response.status === 402) {
            this.showInfo('Transactions are closed')
          } else {
            this.showError('Failed to reverse transaction')
          }
        })
        .finally(() => {
          this.tableLoading = false
          this.reverseConfirmationOpened = false
        })
    },
    handleSearch(search) {
      this.searchTerm = search
      this.getLoanPayments()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.getLoanPayments()
    },
    handlePerPageChange(perPage) {
      if (!this.initializing) {
        this.perPage = perPage
        this.getLoanPayments()
      }
    },
    handleSortChange(sort) {
      this.sort.column = sort.sortBy === 'date' ? 'created_at' : sort.sortBy
      this.sort.order = sort.sortDesc ? 'desc' : 'asc'
      this.getLoanPayments()
    },
    getLoanPayments(showLoader = true, agentId = null) {
      this.tableLoading = showLoader
      const dateFiler = `start=${this.filter.start || ''}&end=${this.filterForSpecificDay ? '' : this.filter.end || ''}`
      const filter = `&${dateFiler}`
      this.$http.get(`susu-savings/loan-payments?agent_id=${agentId || ''}&per_page=${this.perPage}&page=${this.currentPage}&search=${this.searchTerm}&sort_col=${this.sort.column}&sort_order=${this.sort.order}${filter}`)
        .then(res => {
          this.$store.commit('loans/setLoanPayments', res.data)
        }).finally(() => {
          this.initializing = false
          this.tableLoading = false
        })
    },
  },
}
</script>
